import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { chatroomService } from '../services';
import { logger, slugify } from '../services/utils';

const ChatroomContext = createContext();

// State shape
const initialState = {
  channels: [],
  activeChannelId: null,
  messages: {},  // { channelId: [messages] }
  participants: {},  // { channelId: [participants] }
  typingUsers: {},  // { channelId: Set([userId, ...]) }
  bidCycleState: {},  // { channelId: { status, respondingParticipant, ... } }
  bidResults: {},  // { channelId: { cycleId, bids: [...], timestamp } } - current/latest bid cycle
  bidResultsByCycle: {},  // { cycleId: { bids: [...], timestamp } } - persistent storage by cycle
  pausedChannels: {},  // { channelId: boolean } - whether user has paused the channel
  personas: [],  // Available AI personas for adding to channels
  isConnected: false,
  isLoading: false,
  error: null
};

// Actions
const actions = {
  SET_CHANNELS: 'SET_CHANNELS',
  SET_ACTIVE_CHANNEL: 'SET_ACTIVE_CHANNEL',
  SET_CHANNEL_STATE: 'SET_CHANNEL_STATE',
  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  APPEND_MESSAGE_CHUNK: 'APPEND_MESSAGE_CHUNK',
  COMPLETE_MESSAGE_STREAMING: 'COMPLETE_MESSAGE_STREAMING',
  SET_CONNECTED: 'SET_CONNECTED',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_TYPING: 'SET_TYPING',
  ADD_PARTICIPANT: 'ADD_PARTICIPANT',
  UPDATE_PARTICIPANT: 'UPDATE_PARTICIPANT',
  REMOVE_PARTICIPANT: 'REMOVE_PARTICIPANT',
  SET_PARTICIPANTS: 'SET_PARTICIPANTS',
  SET_BID_CYCLE_STATE: 'SET_BID_CYCLE_STATE',
  SET_BID_RESULTS: 'SET_BID_RESULTS',
  CLEAR_BID_RESULTS: 'CLEAR_BID_RESULTS',
  SET_PAUSED: 'SET_PAUSED',
  UPDATE_CHANNEL_SETTINGS: 'UPDATE_CHANNEL_SETTINGS',
  SET_PERSONAS: 'SET_PERSONAS',
  ADD_CHANNEL: 'ADD_CHANNEL'
};

// Reducer
function chatroomReducer(state, action) {
  switch (action.type) {
    case actions.SET_CHANNELS:
      return { ...state, channels: action.payload };

    case actions.SET_ACTIVE_CHANNEL:
      return { ...state, activeChannelId: action.payload };

    case actions.SET_CHANNEL_STATE:
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.channelId]: action.payload.messages
        },
        participants: {
          ...state.participants,
          [action.payload.channelId]: action.payload.participants
        }
      };

    case actions.ADD_MESSAGE: {
      const channelId = action.payload.channel;
      const existingMessages = state.messages[channelId] || [];

      // Check if message already exists (avoid duplicates)
      const messageExists = existingMessages.some(msg => msg.id === action.payload.id);
      if (messageExists) {
        return state;
      }

      return {
        ...state,
        messages: {
          ...state.messages,
          [channelId]: [...existingMessages, action.payload]
        }
      };
    }

    case actions.UPDATE_MESSAGE: {
      // Update an existing message (e.g., when images are generated)
      if (!action.payload || !action.payload.channel) {
        console.warn('UPDATE_MESSAGE received with invalid payload:', action.payload);
        return state;
      }
      const updateChannelId = action.payload.channel;
      const channelMessages = state.messages[updateChannelId] || [];

      return {
        ...state,
        messages: {
          ...state.messages,
          [updateChannelId]: channelMessages.map(msg =>
            msg.id === action.payload.id ? action.payload : msg
          )
        }
      };
    }

    case actions.APPEND_MESSAGE_CHUNK: {
      // Update a streaming message with new content
      const { messageId, channelId, accumulated } = action.payload;
      const streamChannelMessages = state.messages[channelId] || [];

      return {
        ...state,
        messages: {
          ...state.messages,
          [channelId]: streamChannelMessages.map(msg =>
            msg.id === messageId
              ? { ...msg, content: accumulated, status: 'generating' }
              : msg
          )
        }
      };
    }

    case actions.COMPLETE_MESSAGE_STREAMING: {
      // Mark a streaming message as complete
      const { messageId: completeMessageId, channelId: completeChannelId, accumulated: finalContent } = action.payload;
      const completeChannelMessages = state.messages[completeChannelId] || [];

      return {
        ...state,
        messages: {
          ...state.messages,
          [completeChannelId]: completeChannelMessages.map(msg =>
            msg.id === completeMessageId
              ? { ...msg, content: finalContent, status: 'published' }
              : msg
          )
        }
      };
    }

    case actions.SET_CONNECTED:
      return { ...state, isConnected: action.payload };

    case actions.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case actions.SET_ERROR:
      return { ...state, error: action.payload };

    case actions.SET_TYPING:
      const { channelId: typingChannelId, userId, isTyping } = action.payload;
      const typingSet = new Set(state.typingUsers[typingChannelId] || []);
      if (isTyping) {
        typingSet.add(userId);
      } else {
        typingSet.delete(userId);
      }
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [typingChannelId]: typingSet
        }
      };

    case actions.SET_PARTICIPANTS: {
      const { channelId: pChannelId, participants: pList } = action.payload;
      return {
        ...state,
        participants: {
          ...state.participants,
          [pChannelId]: pList
        }
      };
    }

    case actions.ADD_PARTICIPANT: {
      const { channelId: addChannelId, participant } = action.payload;
      const existingParticipants = state.participants[addChannelId] || [];
      // Check if participant already exists
      const participantExists = existingParticipants.some(p => p.id === participant.id);
      if (participantExists) {
        return state;
      }
      return {
        ...state,
        participants: {
          ...state.participants,
          [addChannelId]: [...existingParticipants, participant]
        }
      };
    }

    case actions.UPDATE_PARTICIPANT: {
      const { channelId: updateChannelId, participant: updatedParticipant } = action.payload;
      const channelParticipants = state.participants[updateChannelId] || [];
      return {
        ...state,
        participants: {
          ...state.participants,
          [updateChannelId]: channelParticipants.map(p =>
            p.id === updatedParticipant.id ? updatedParticipant : p
          )
        }
      };
    }

    case actions.REMOVE_PARTICIPANT: {
      const { channelId: removeChannelId, participantId } = action.payload;
      const currentParticipants = state.participants[removeChannelId] || [];
      return {
        ...state,
        participants: {
          ...state.participants,
          [removeChannelId]: currentParticipants.filter(p => p.id !== participantId)
        }
      };
    }

    case actions.SET_BID_CYCLE_STATE: {
      const { channelId: bidChannelId, ...bidState } = action.payload;
      return {
        ...state,
        bidCycleState: {
          ...state.bidCycleState,
          [bidChannelId]: bidState
        }
      };
    }

    case actions.SET_BID_RESULTS: {
      const { channelId: resultsChannelId, cycleId, bids } = action.payload;
      return {
        ...state,
        // Keep the ephemeral "current" bid results for the status indicator
        bidResults: {
          ...state.bidResults,
          [resultsChannelId]: {
            cycleId,
            bids,
            timestamp: Date.now()
          }
        },
        // Also store persistently by cycle_id for display with messages
        bidResultsByCycle: {
          ...state.bidResultsByCycle,
          [cycleId]: {
            bids,
            timestamp: Date.now()
          }
        }
      };
    }

    case actions.CLEAR_BID_RESULTS: {
      const { channelId: clearChannelId } = action.payload;
      const newBidResults = { ...state.bidResults };
      delete newBidResults[clearChannelId];
      return {
        ...state,
        bidResults: newBidResults
      };
    }

    case actions.SET_PAUSED: {
      const { channelId: pauseChannelId, paused } = action.payload;
      return {
        ...state,
        pausedChannels: {
          ...state.pausedChannels,
          [pauseChannelId]: paused
        }
      };
    }

    case actions.UPDATE_CHANNEL_SETTINGS: {
      const { channelId: settingsChannelId, settings } = action.payload;
      return {
        ...state,
        channels: state.channels.map(ch =>
          ch.id === settingsChannelId
            ? { ...ch, settings: { ...ch.settings, ...settings } }
            : ch
        )
      };
    }

    case actions.SET_PERSONAS:
      return { ...state, personas: action.payload };

    case actions.ADD_CHANNEL:
      return { ...state, channels: [action.payload, ...state.channels] };

    default:
      return state;
  }
}

// Provider component
export function ChatroomProvider({ children }) {
  const [state, dispatch] = useReducer(chatroomReducer, initialState);

  // Load channels on mount
  useEffect(() => {
    loadChannels();
  }, []);

  // WebSocket setup
  useEffect(() => {
    chatroomService.connect();

    // Register handlers
    const handleOpen = () => {
      dispatch({ type: actions.SET_CONNECTED, payload: true });
    };

    const handleClose = () => {
      dispatch({ type: actions.SET_CONNECTED, payload: false });
    };

    const handleMessage = (data) => {
      switch (data.type) {
        case 'channel_state':
          dispatch({
            type: actions.SET_CHANNEL_STATE,
            payload: {
              channelId: data.data.channel_id,
              messages: data.data.messages,
              participants: data.data.participants
            }
          });
          break;

        case 'message_created':
          dispatch({ type: actions.ADD_MESSAGE, payload: data.data });
          break;

        case 'message_updated':
          // Handle message updates (e.g., when images are generated)
          dispatch({ type: actions.UPDATE_MESSAGE, payload: data.data });
          break;

        case 'message_streaming':
          // Handle streaming LLM response chunks
          if (data.data.is_complete) {
            dispatch({
              type: actions.COMPLETE_MESSAGE_STREAMING,
              payload: {
                messageId: data.data.message_id,
                channelId: data.data.channel_id,
                accumulated: data.data.accumulated
              }
            });
          } else {
            dispatch({
              type: actions.APPEND_MESSAGE_CHUNK,
              payload: {
                messageId: data.data.message_id,
                channelId: data.data.channel_id,
                chunk: data.data.chunk,
                accumulated: data.data.accumulated
              }
            });
          }
          break;

        case 'typing_indicator':
          dispatch({
            type: actions.SET_TYPING,
            payload: {
              channelId: data.channel_id,
              userId: data.user_id,
              isTyping: data.is_typing
            }
          });
          break;

        case 'participant_added':
          dispatch({
            type: actions.ADD_PARTICIPANT,
            payload: {
              channelId: data.channel_id,
              participant: data.participant
            }
          });
          break;

        case 'participant_updated':
          dispatch({
            type: actions.UPDATE_PARTICIPANT,
            payload: {
              channelId: data.channel_id,
              participant: data.participant
            }
          });
          break;

        case 'participant_removed':
          dispatch({
            type: actions.REMOVE_PARTICIPANT,
            payload: {
              channelId: data.channel_id,
              participantId: data.participant_id
            }
          });
          break;

        case 'bid_cycle_started':
          // Clear any stale bid results from previous cycle
          dispatch({
            type: actions.CLEAR_BID_RESULTS,
            payload: { channelId: data.channel_id }
          });
          dispatch({
            type: actions.SET_BID_CYCLE_STATE,
            payload: {
              channelId: data.channel_id,
              status: 'collecting',
              participantCount: data.participant_count,
              cycleId: data.cycle_id
            }
          });
          break;

        case 'bid_cycle_ai_responding':
          dispatch({
            type: actions.SET_BID_CYCLE_STATE,
            payload: {
              channelId: data.channel_id,
              status: 'responding',
              respondingParticipant: data.participant_name,
              participantId: data.participant_id
            }
          });
          break;

        case 'bid_cycle_complete':
          dispatch({
            type: actions.SET_BID_CYCLE_STATE,
            payload: {
              channelId: data.channel_id,
              status: 'idle'
            }
          });
          break;

        case 'bid_cycle_dormant':
          dispatch({
            type: actions.SET_BID_CYCLE_STATE,
            payload: {
              channelId: data.channel_id,
              status: 'dormant',
              maxBid: data.max_bid,
              threshold: data.threshold
            }
          });
          break;

        case 'bid_cycle_results':
          dispatch({
            type: actions.SET_BID_RESULTS,
            payload: {
              channelId: data.channel_id,
              cycleId: data.cycle_id,
              bids: data.bids
            }
          });
          break;

        case 'channel_settings_updated':
          dispatch({
            type: actions.UPDATE_CHANNEL_SETTINGS,
            payload: {
              channelId: data.channel_id,
              settings: data.settings
            }
          });
          break;

        case 'channel_paused':
          dispatch({
            type: actions.SET_PAUSED,
            payload: {
              channelId: data.channel_id,
              paused: true
            }
          });
          break;

        case 'channel_unpaused':
          dispatch({
            type: actions.SET_PAUSED,
            payload: {
              channelId: data.channel_id,
              paused: false
            }
          });
          break;

        case 'error':
          dispatch({ type: actions.SET_ERROR, payload: data.message });
          break;

        default:
          logger.debug('ChatroomContext', 'Unhandled message type', data.type);
      }
    };

    chatroomService.on('open', handleOpen);
    chatroomService.on('close', handleClose);
    chatroomService.on('message', handleMessage);

    // Cleanup
    return () => {
      chatroomService.off('open', handleOpen);
      chatroomService.off('close', handleClose);
      chatroomService.off('message', handleMessage);
      chatroomService.disconnect();
    };
  }, []);

  // Actions
  const loadChannels = useCallback(async () => {
    dispatch({ type: actions.SET_LOADING, payload: true });
    try {
      const channels = await chatroomService.getChannels();
      dispatch({ type: actions.SET_CHANNELS, payload: channels });
    } catch (error) {
      dispatch({ type: actions.SET_ERROR, payload: 'Failed to load channels' });
      logger.error('ChatroomContext', 'Failed to load channels', error);
    } finally {
      dispatch({ type: actions.SET_LOADING, payload: false });
    }
  }, []);

  const selectChannel = useCallback((channelId) => {
    // Leave current channel
    if (state.activeChannelId) {
      chatroomService.leaveChannel(state.activeChannelId);
    }

    // Join new channel
    if (channelId) {
      chatroomService.joinChannel(channelId);
    }

    dispatch({ type: actions.SET_ACTIVE_CHANNEL, payload: channelId });
  }, [state.activeChannelId]);

  const sendMessage = useCallback(async (content, { skipBidTrigger = false } = {}) => {
    if (!state.activeChannelId) return;

    try {
      // HTTP POST - message will be broadcast via WebSocket
      await chatroomService.sendMessage(state.activeChannelId, content, { skipBidTrigger });
    } catch (error) {
      dispatch({ type: actions.SET_ERROR, payload: 'Failed to send message' });
      logger.error('ChatroomContext', 'Failed to send message', error);
    }
  }, [state.activeChannelId]);

  // Pause the channel (server-side pause to stop LLM-to-LLM triggering)
  const pauseChannel = useCallback(async (channelId) => {
    try {
      await chatroomService.pauseChannel(channelId);
      dispatch({
        type: actions.SET_PAUSED,
        payload: { channelId, paused: true }
      });
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to pause channel', error);
      dispatch({ type: actions.SET_ERROR, payload: 'Failed to pause channel' });
    }
  }, []);

  // Unpause the channel (server-side unpause to resume LLM-to-LLM triggering)
  const unpauseChannel = useCallback(async (channelId) => {
    try {
      await chatroomService.unpauseChannel(channelId);
      dispatch({
        type: actions.SET_PAUSED,
        payload: { channelId, paused: false }
      });
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to unpause channel', error);
      dispatch({ type: actions.SET_ERROR, payload: 'Failed to unpause channel' });
    }
  }, []);

  // Update channel settings (moderators only)
  const updateChannelSettings = useCallback(async (channelId, settings) => {
    try {
      await chatroomService.updateChannelSettings(channelId, settings);
      // WebSocket broadcast will update state
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to update channel settings', error);
      throw error;
    }
  }, []);

  const sendTyping = useCallback((isTyping) => {
    if (!state.activeChannelId) return;
    chatroomService.sendTypingIndicator(state.activeChannelId, isTyping);
  }, [state.activeChannelId]);

  // Find a channel by space and channel slug
  const findChannelBySlug = useCallback((spaceSlug, channelSlug) => {
    return state.channels.find(channel =>
      slugify(channel.space_name || '') === spaceSlug &&
      slugify(channel.name) === channelSlug
    );
  }, [state.channels]);

  // Participant management actions
  const loadParticipants = useCallback(async (channelId) => {
    try {
      const participants = await chatroomService.getParticipants(channelId);
      dispatch({
        type: actions.SET_PARTICIPANTS,
        payload: { channelId, participants }
      });
      return participants;
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to load participants', error);
      throw error;
    }
  }, []);

  const inviteParticipant = useCallback(async (channelId, { userId, role = 'writer' }) => {
    try {
      const participant = await chatroomService.inviteParticipant(channelId, { userId, role });
      // Optimistic update not needed - WebSocket broadcast will update state
      return participant;
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to invite participant', error);
      throw error;
    }
  }, []);

  const updateParticipantRole = useCallback(async (channelId, participantId, role) => {
    try {
      const participant = await chatroomService.updateParticipant(channelId, participantId, { role });
      // WebSocket broadcast will update state
      return participant;
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to update participant', error);
      throw error;
    }
  }, []);

  const removeParticipant = useCallback(async (channelId, participantId) => {
    try {
      await chatroomService.removeParticipant(channelId, participantId);
      // WebSocket broadcast will update state
      return true;
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to remove participant', error);
      throw error;
    }
  }, []);

  const leaveChannelAsParticipant = useCallback(async (channelId, participantId) => {
    try {
      await chatroomService.leaveChannelAsParticipant(channelId, participantId);
      // Reload channels to reflect the change
      await loadChannels();
      return true;
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to leave channel', error);
      throw error;
    }
  }, [loadChannels]);

  const acceptInvitation = useCallback(async (channelId, participantId) => {
    try {
      const participant = await chatroomService.acceptInvitation(channelId, participantId);
      // Reload channels to update current_user_participant
      await loadChannels();
      return participant;
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to accept invitation', error);
      throw error;
    }
  }, [loadChannels]);

  const declineInvitation = useCallback(async (channelId, participantId) => {
    try {
      await chatroomService.declineInvitation(channelId, participantId);
      // Reload channels to reflect the change
      await loadChannels();
      return true;
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to decline invitation', error);
      throw error;
    }
  }, [loadChannels]);

  const searchInvitableUsers = useCallback(async (channelId, search = '') => {
    try {
      return await chatroomService.searchInvitableUsers(channelId, search);
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to search users', error);
      throw error;
    }
  }, []);

  // Load available personas
  const loadPersonas = useCallback(async () => {
    try {
      const personas = await chatroomService.getPersonas();
      dispatch({ type: actions.SET_PERSONAS, payload: personas });
      return personas;
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to load personas', error);
      throw error;
    }
  }, []);

  // Create a new channel with optional personas
  const createChannel = useCallback(async (name, description = '', settings = {}, personaIds = []) => {
    try {
      // Create the channel
      const channel = await chatroomService.createChannel(name, description, settings);

      // Add personas to the channel if any were selected
      for (const personaId of personaIds) {
        try {
          await chatroomService.addPersonaToChannel(channel.id, personaId);
        } catch (error) {
          logger.error('ChatroomContext', `Failed to add persona ${personaId} to channel`, error);
          // Continue adding other personas even if one fails
        }
      }

      // Reload channels to get the full channel data with participants
      await loadChannels();

      return channel;
    } catch (error) {
      logger.error('ChatroomContext', 'Failed to create channel', error);
      throw error;
    }
  }, [loadChannels]);

  // Get the active channel object
  const activeChannel = state.channels.find(c => c.id === state.activeChannelId) || null;

  // Get current user's participant info for active channel
  const currentUserParticipant = activeChannel?.current_user_participant || null;

  const value = {
    ...state,
    activeChannel,
    currentUserParticipant,
    loadChannels,
    selectChannel,
    sendMessage,
    sendTyping,
    findChannelBySlug,
    // Participant management
    loadParticipants,
    inviteParticipant,
    updateParticipantRole,
    removeParticipant,
    leaveChannelAsParticipant,
    acceptInvitation,
    declineInvitation,
    searchInvitableUsers,
    // Pause functionality
    pauseChannel,
    unpauseChannel,
    // Channel settings
    updateChannelSettings,
    // Channel creation
    loadPersonas,
    createChannel
  };

  return (
    <ChatroomContext.Provider value={value}>
      {children}
    </ChatroomContext.Provider>
  );
}

// Hook
export function useChatroom() {
  const context = useContext(ChatroomContext);
  if (!context) {
    throw new Error('useChatroom must be used within ChatroomProvider');
  }
  return context;
}
